import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { paymentService } from '../../services/paymentService';
import { vendorService } from '../../services/vendorService';
import bankAccountService from '../../services/bankAccountService';
import { companyService } from '../../services/companyService';
import { api } from '../../services/api';
import { CreatePaymentData, Payment } from '../../types/payment';
import { PAYMENT_METHODS } from '../../types/payment';
import { formatCurrency } from '../../utils/formatters';
import { TransactionReceipt } from '../payments/TransactionReceipt';

interface BillPaymentFormProps {
  billId?: string;
  vendorId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

// ─── shared style tokens ─────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 20,
  fontWeight: 300,
  color: '#1c2033',
  margin: 0,
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const focusIn = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = 'rgba(217,119,6,0.35)';
};
const focusOut = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = 'rgba(92,78,51,0.25)';
};

export const BillPaymentForm: React.FC<BillPaymentFormProps> = ({
  billId,
  vendorId,
  onSuccess,
  onCancel
}) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CreatePaymentData>({
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    reference: '',
    notes: '',
    vendorId: vendorId || '',
    billId: billId || '',
    bankAccountId: ''
  });

  // Update form data when vendorId prop changes
  React.useEffect(() => {
    if (vendorId && formData.vendorId !== vendorId) {
      setFormData(prev => ({
        ...prev,
        vendorId: vendorId,
        billId: billId || ''
      }));
    }
  }, [vendorId, billId, formData.vendorId]);

  const [errors, setErrors] = useState<string[]>([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [createdPayment, setCreatedPayment] = useState<Payment | null>(null);

  // Load vendors for dropdown
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getVendors({ limit: 1000 })
  });

  // Fetch bank accounts for selection
  const { data: bankAccountsResponse, isLoading: bankAccountsLoading, error: bankAccountsError } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => bankAccountService.getBankAccounts({ isActive: true })
  });

  // Fetch company profile
  const { data: companyProfile } = useQuery({
    queryKey: ['company', 'profile'],
    queryFn: companyService.getProfile
  });

  // Load all bills for selected vendor
  const { data: vendorBills, isLoading: billsLoading } = useQuery({
    queryKey: ['vendor-all-bills', formData.vendorId],
    queryFn: async () => {
      const response = await api.get(`/bills?vendorId=${formData.vendorId}`);
      return response.data.data;
    },
    enabled: !!formData.vendorId
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreatePaymentData) => {
      const response = await api.post('/payments', data);
      return response.data;
    },
    onSuccess: (payment: Payment) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-unpaid-bills'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-all-bills'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      setCreatedPayment(payment);
      setShowReceipt(true);
    },
    onError: (error: any) => {
      setErrors([error.response?.data?.message || 'Failed to create payment']);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    const validationErrors = paymentService.validatePaymentData(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!formData.bankAccountId) {
      setErrors(['Please select a bank account']);
      return;
    }

    createMutation.mutate(formData);
  };

  const handleVendorChange = (newVendorId: string) => {
    setFormData(prev => ({
      ...prev,
      vendorId: newVendorId,
      billId: '',
      amount: 0
    }));
  };

  const handleBillChange = (newBillId: string) => {
    const selectedBill = vendorBills?.find((bill: any) => bill.id === newBillId);
    setFormData(prev => ({
      ...prev,
      billId: newBillId,
      amount: selectedBill ? selectedBill.remainingAmount : 0
    }));
  };

  const selectedBill = vendorBills?.find((bill: any) => bill.id === formData.billId);
  const selectedVendor = vendorsData?.data?.find((vendor: any) => vendor.id === formData.vendorId);
  const selectedBankAccount = bankAccountsResponse?.accounts?.find(acc => acc.id === formData.bankAccountId);

  // Debug logging
  console.log('🏦 Bank Accounts Debug:', {
    bankAccountsResponse,
    bankAccountsLoading,
    bankAccountsError,
    accounts: bankAccountsResponse?.accounts
  });

  console.log('👤 Vendor Debug:', {
    vendorIdProp: vendorId,
    formDataVendorId: formData.vendorId,
    vendorsLoading,
    vendorsData: vendorsData?.data,
    selectedVendor: selectedVendor
  });

  const handlePrint = () => {
    const printContent = document.getElementById('transaction-receipt');
    const originalContent = document.body.innerHTML;
    if (printContent) {
      document.body.innerHTML = printContent.outerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    }
  };

  const handleReceiptClose = () => {
    setShowReceipt(false);
    onSuccess();
  };

  if (showReceipt && createdPayment) {
    const companyAddress = [
      companyProfile?.address,
      companyProfile?.city,
      companyProfile?.state,
      companyProfile?.postalCode,
      companyProfile?.country
    ].filter(Boolean).join(' ');

    return (
      <TransactionReceipt
        payment={createdPayment}
        companyName={companyProfile?.name || 'Company Name'}
        companyAddress={companyAddress || 'Company Address'}
        companyPhone={companyProfile?.phone || ''}
        companyEmail={companyProfile?.email || ''}
        companyLogo={companyProfile?.logo || undefined}
        onClose={handleReceiptClose}
        onPrint={handlePrint}
      />
    );
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto',
      zIndex: 50,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 16px 40px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 640,
        background: '#ffffff',
        border: '1px solid rgba(92,78,51,0.15)',
        borderRadius: 8,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 28px 20px',
          borderBottom: '1px solid rgba(92,78,51,0.15)',
          background: '#faf7ee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={sectionTitleStyle}>Record Bill Payment</h3>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginTop: 4 }}>
              Process a payment against a vendor bill
            </p>
          </div>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              color: '#8a8778',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: 4,
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#8a8778')}
            onMouseLeave={e => (e.currentTarget.style.color = '#c9c2ab')}
            title="Close"
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Errors */}
          {errors.length > 0 && (
            <div style={{
              background: 'rgba(201,90,90,0.08)',
              border: '1px solid rgba(201,90,90,0.2)',
              borderRadius: 4,
              padding: 14,
            }}>
              <ul style={{ margin: 0, paddingLeft: 16, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c95a5a' }}>
                {errors.map((error, index) => <li key={index}>{error}</li>)}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Vendor & Bill Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Vendor <span style={{ color: '#c95a5a' }}>*</span></label>
                <select
                  value={formData.vendorId}
                  onChange={(e) => handleVendorChange(e.target.value)}
                  style={{
                    ...inputStyle,
                    appearance: 'none',
                    ...(!!vendorId || vendorsLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                  }}
                  required
                  disabled={!!vendorId || vendorsLoading}
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  <option value="">{vendorsLoading ? 'Loading vendors...' : 'Select vendor'}</option>
                  {vendorsData?.data?.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                  ))}
                </select>
                {selectedVendor && !!vendorId && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', marginTop: 4 }}>
                    {selectedVendor.name}
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle}>Bill <span style={{ color: '#8a8778' }}>(Optional)</span></label>
                <select
                  value={formData.billId}
                  onChange={(e) => handleBillChange(e.target.value)}
                  style={{
                    ...inputStyle,
                    appearance: 'none',
                    ...(!formData.vendorId || billsLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                  disabled={!formData.vendorId || billsLoading}
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  <option value="">
                    {billsLoading ? 'Loading bills...' : 'Select bill (or general payment)'}
                  </option>
                  {vendorBills?.map((bill: any) => (
                    <option key={bill.id} value={bill.id}>
                      {bill.billNumber} — {bill.status === 'PAID' ? 'PAID' : `${formatCurrency(bill.remainingAmount)} remaining`} — {bill.status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Selected Bill Detail Card */}
            {selectedBill && (
              <div style={{
                padding: '12px 16px',
                background: '#f4efe2',
                border: '1px solid rgba(92,78,51,0.22)',
                borderRadius: 4,
              }}>
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 9,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#6b6a5c',
                  marginBottom: 10,
                }}>
                  Bill Details
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                  {[
                    ['Bill Number', selectedBill.billNumber],
                    ['Total', formatCurrency(selectedBill.total)],
                    ['Paid', formatCurrency(selectedBill.paidAmount)],
                    ['Remaining', formatCurrency(selectedBill.remainingAmount)],
                  ].map(([k, v]) => (
                    <div key={k} style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                      <span style={{ color: '#6b6a5c' }}>{k}: </span>{v}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amount & Date Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Payment Amount <span style={{ color: '#c95a5a' }}>*</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 11,
                    color: '#6b6a5c',
                    pointerEvents: 'none',
                  }}>
                    BD
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedBill && selectedBill.remainingAmount > 0 ? selectedBill.remainingAmount : undefined}
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    required
                    style={{ ...inputStyle, paddingLeft: 30 }}
                    onFocus={focusIn}
                    onBlur={focusOut}
                  />
                </div>
                {selectedBill && selectedBill.remainingAmount > 0 && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginTop: 4 }}>
                    Maximum: {formatCurrency(selectedBill.remainingAmount)}
                  </p>
                )}
                {selectedBill && selectedBill.remainingAmount === 0 && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', marginTop: 4 }}>
                    This bill is already fully paid. Additional payments will still be recorded.
                  </p>
                )}
              </div>

              <div>
                <label style={labelStyle}>Payment Date <span style={{ color: '#c95a5a' }}>*</span></label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentDate: e.target.value }))}
                  required
                  style={{ ...inputStyle, colorScheme: 'light' }}
                  onFocus={focusIn}
                  onBlur={focusOut}
                />
              </div>
            </div>

            {/* Bank Account & Method Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={labelStyle}>Bank Account <span style={{ color: '#c95a5a' }}>*</span></label>
                <select
                  value={formData.bankAccountId}
                  onChange={(e) => setFormData(prev => ({ ...prev, bankAccountId: e.target.value }))}
                  required
                  disabled={bankAccountsLoading}
                  style={{
                    ...inputStyle,
                    appearance: 'none',
                    ...(bankAccountsLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  <option value="">{bankAccountsLoading ? 'Loading bank accounts...' : 'Select a bank account'}</option>
                  {bankAccountsResponse?.accounts?.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.accountName} — {account.bankName} ({formatCurrency(account.balance)})
                    </option>
                  ))}
                </select>
                {bankAccountsError && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#c95a5a', marginTop: 4 }}>
                    Error loading bank accounts: {bankAccountsError.message || 'Unknown error'}
                  </p>
                )}
                {!bankAccountsLoading && !bankAccountsError && (!bankAccountsResponse?.accounts || bankAccountsResponse.accounts.length === 0) && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', marginTop: 4 }}>
                    No bank accounts found. Please create a bank account first.
                  </p>
                )}
                {selectedBankAccount && (
                  <div style={{
                    marginTop: 6,
                    padding: '8px 10px',
                    background: 'rgba(241,245,249,1)',
                    border: '1px solid rgba(92,78,51,0.2)',
                    borderRadius: 4,
                  }}>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#5b5a52' }}>
                      <span style={{ color: '#6b6a5c' }}>Balance: </span>
                      {formatCurrency(selectedBankAccount.balance || 0)}
                    </div>
                    <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#5a4a35', marginTop: 2 }}>
                      {selectedBankAccount.bankName} — ****{selectedBankAccount.accountNumber.slice(-4)}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label style={labelStyle}>Payment Method</label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  style={{ ...inputStyle, appearance: 'none' }}
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reference */}
            <div>
              <label style={labelStyle}>Reference</label>
              <input
                type="text"
                value={formData.reference || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Check number, transaction ID, etc."
                style={inputStyle}
                onFocus={focusIn}
                onBlur={focusOut}
              />
            </div>

            {/* Notes */}
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                rows={3}
                value={formData.notes || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes about this payment..."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
                onFocus={focusIn}
                onBlur={focusOut}
              />
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              paddingTop: 12,
              borderTop: '1px solid rgba(92,78,51,0.15)',
            }}>
              <button
                type="button"
                onClick={onCancel}
                style={ghostBtnStyle}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                style={{
                  ...primaryBtnStyle,
                  ...(createMutation.isPending ? { opacity: 0.7, cursor: 'not-allowed' } : {})
                }}
              >
                {createMutation.isPending ? 'Recording...' : 'Record Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
